import { Inject, Injectable, InjectionToken } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { type InvitationRepository } from '@telespot/domain';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, switchMap, tap, map, take, filter } from 'rxjs/operators';
import { CreateInvitation, CreateInvitationHandler } from '@telespot/management/use-cases';
import { PaginatedStore, PaginatedStateModel } from '@shared/ui';

export interface InviteData {
  id: string;
  uses: number;
  code: string;
  link: string;
  createdAt: Date;
}

export interface InvitesStoreState extends PaginatedStateModel<InviteData> {
  organizationId?: string;
  organizationName?: string;
}

export interface ContextualizedInvite {
  code: string;
  organizationName: string;
  initialRoles: string[];
}

export const DEFAULT_PAGE_SIZE = 10;
export const CREATE_INVITATION_USE_CASE = new InjectionToken<CreateInvitationHandler>('CREATE_INVITATION_USE_CASE');
export const INVITATION_REPOSITORY = new InjectionToken<InvitationRepository>('INVITATION_REPOSITORY');

@Injectable()
export class InvitesStore extends PaginatedStore<InviteData, InvitesStoreState> {
  // Store-specific selectors
  public readonly organizationId$ = this.select((state) => state.organizationId);
  public readonly organizationName$ = this.select((state) => state.organizationName);

  constructor(
    @Inject(INVITATION_REPOSITORY) private readonly invitationRepository: InvitationRepository,
    @Inject(CREATE_INVITATION_USE_CASE) private readonly createInvitationUseCase: CreateInvitationHandler
  ) {
    super({
      organizationId: undefined,
      organizationName: undefined,
    });

    // Cascading effects
    this.onOrganizationContextDefined(this.organizationId$);
  }

  // Backward compatibility aliases
  public readonly invites$ = this.items$;
  public readonly totalInviteCount$ = this.totalCount$;
  public readonly previousInviteTokens$ = this.previousTokens$;
  public readonly nextInviteToken$ = this.nextToken$;
  public readonly invitesLoaded$ = this.select((state) => !state.loading);
  public readonly loadingInvites$ = this.loading$;

  // Store-specific updater
  public readonly setOrganizationContext = this.updater(
    (state, context: { organizationId: string; organizationName: string }) => ({
      ...state,
      organizationId: context.organizationId,
      organizationName: context.organizationName,
    })
  );

  // Effects
  public readonly listOrganizationInvites = this.loadMoreItems;

  // Initialize invites for organization
  private readonly onOrganizationContextDefined = this.effect((action$: Observable<string | undefined>) =>
    action$.pipe(
      filter((organizationId) => !!organizationId),
      tap(() => this.initializeItems())
    )
  );

  public readonly createInvite = this.effect(
    (
      action$: Observable<{
        code: string;
        uses: number;
        initialWorkspacesIds: string[];
        initialRolesIds: string[];
      }>
    ) =>
      action$.pipe(
        concatLatestFrom(() => [this.organizationId$]),
        switchMap(([attributes, organizationId]) => {
          if (!organizationId) {
            return EMPTY;
          }
          return from(
            this.createInvitationUseCase.execute(
              new CreateInvitation({
                organizationId: organizationId,
                code: attributes.code,
                uses: attributes.uses,
                initialWorkspaces: attributes.initialWorkspacesIds,
                initialRoles: attributes.initialRolesIds,
              })
            )
          ).pipe(
            tap(() => {
              // After creation, reload the list and re-count the invites in the organization
              this.countItems();
              this.initializeItems();
            }),
            catchError((error) => {
              this.setError(error.message);
              return EMPTY;
            })
          );
        })
      )
  );

  public readonly removeInvite = this.deleteItem;
  public readonly countOrganizationInvites = this.countItems;

  public async getInvite(inviteCode: string): Promise<ContextualizedInvite | undefined> {
    const invite = await this.invitationRepository.getByCode(inviteCode);

    if (!invite) {
      return undefined;
    }

    const organizationName = await this.organizationName$.pipe(take(1)).toPromise();
    if (!organizationName) {
      return undefined;
    }

    return {
      code: inviteCode,
      organizationName: organizationName,
      initialRoles: invite.initialRoles.map((role) => role.name),
    };
  }

  // Implement abstract methods from PaginatedStore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async countRepository(_searchTerm?: string): Promise<number> {
    const organizationId = this.get((state) => state.organizationId);
    if (!organizationId) return 0;
    return this.invitationRepository.count(organizationId);
  }

  protected async deleteRepositoryItem(itemId: string): Promise<boolean> {
    try {
      await this.invitationRepository.delete(itemId);
      return true;
    } catch {
      return false;
    }
  }

  protected listFlow(
    limit: number,
    lastItem: InviteData | undefined,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _searchTerm: string | undefined
  ): Observable<InviteData[]> {
    const organizationId = this.get((state) => state.organizationId);
    if (!organizationId) return from([]);

    return from(this.invitationRepository.list(organizationId, limit, lastItem?.createdAt)).pipe(
      map((invites) =>
        invites.map((invite) => ({
          id: invite.id ?? '',
          code: invite.code,
          uses: invite.uses,
          link: `${window.location.origin}/register?invite=${invite.code}`,
          createdAt: invite.createdAt,
        }))
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected processDeferredAttributes(_itemId: string): Observable<Partial<InviteData>> {
    // No counts/metadata are needed to be loaded for the invites store
    return of();
  }
}
